import { useState, useEffect } from 'react';
import { Heart, Phone, Video, Star, MapPin, Briefcase, X, CalendarClock, Globe, MessageCircle } from 'lucide-react';
import { getSocialLinks, detectPlatform, ensureProtocol } from '../../lib/socialLinks';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  getExpertAvailability,
  getExpertRate,
  getExpertName,
  getExpertExpertise,
  getExpertBio,
  getExpertIndustry,
  getExpertTitle,
  getExpertPhoto,
  formatRate,
  parseExpertCallModes,
  getAvailabilityDisplayText,
} from '../../lib/expertUtils';

export default function ExpandedProfile({
  expert,
  onCall,
  onClose,
  canMakeVideoCalls,
  isFavorited,
  onToggleFavorite,
  favoriteLoading,
  canFavorite,
  currentUserId,
  currentUserRole,
}) {
  const [reviews, setReviews] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const expertId = expert?.uid || expert?.id;
    if (!expertId) return;
    const fetchReviews = async () => {
      try {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('expertId', '==', expertId),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snap = await getDocs(reviewsQuery);
        // Filter out maven_to_seeker reviews (those belong on the seeker, not the maven)
        setReviews(snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.reviewType !== 'maven_to_seeker')
        );
      } catch (err) {
        console.error('Error loading reviews:', err);
      }
    };
    fetchReviews();
  }, [expert?.uid, expert?.id]);

  const availability = getExpertAvailability(expert);
  const rate = getExpertRate(expert);
  const name = getExpertName(expert);
  const expertise = getExpertExpertise(expert);
  const bio = getExpertBio(expert);
  const industry = getExpertIndustry(expert);
  const title = getExpertTitle(expert);
  const sex = expert.sex || expert.expertProfile?.sex || '';
  const { supportsAudio, supportsVideo } = parseExpertCallModes(expert);
  const rating = expert.averageRating || expert.rating || 0;
  const totalCalls = expert.totalCalls || expert.callsCompleted || 0;
  const totalReviews = expert.totalReviews || 0;
  const favoriteCount = Number(expert.seekerFavoritesCount || 0);
  const photoURL = getExpertPhoto(expert);
  const expertId = expert?.uid || expert?.id;
  const isOnline = availability === 'online';
  const isBusy = availability === 'busy';
  const isOffline = availability === 'offline';
  const isApproved = expert.applicationStatus === 'approved';
  const isTrusted = expert.trustedPayoutVerified === true;
  const chatEnabled = expert.chatEnabled !== false; // default-on; only hide if explicitly disabled
  const isSeekerRole = currentUserRole === 'quester' || currentUserRole === 'seeker' || currentUserRole === 'sales';
  // Calling AND chatting are open to any signed-in user (mavens can call/chat
  // other mavens now), as long as it's not their own profile. Chat billing is
  // ID-based server-side (initiator pays, recipient earns), so maven→maven is safe.
  const canChat = chatEnabled && currentUserId && currentUserId !== expertId;
  const canCall = Boolean(isApproved && currentUserId && currentUserId !== expertId);

  const handleStartChat = async () => {
    if (!currentUserId || !expertId) return;
    setChatLoading(true);
    try {
      const response = await fetch('/api/chat/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questerId: currentUserId, expertId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start chat');
      router.push(`/chat/${data.chatId}`);
    } catch (err) {
      console.error('Error starting chat:', err);
      alert(err.message);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero Photo */}
        <div className="relative h-64 bg-gradient-to-br from-emerald-200 to-blue-200 dark:from-emerald-900/40 dark:to-blue-900/40">
          {photoURL ? (
            <Image
              src={photoURL}
              alt={name}
              fill
              sizes="512px"
              className="object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
                <span className="text-white text-5xl font-bold">
                  {name.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white dark:from-gray-900 to-transparent" />

          {/* Availability badge */}
          <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm text-xs font-bold ${
            isOnline ? 'bg-emerald-500/90 text-white' :
            isBusy ? 'bg-orange-500/90 text-white' :
            'bg-gray-500/90 text-white'
          }`}>
            {isOnline && <span className="w-2 h-2 bg-white rounded-full animate-pulse" />}
            {getAvailabilityDisplayText(availability)}
          </div>
        </div>

        {/* Profile Info */}
        <div className="px-6 pb-6 -mt-8 relative">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{name}</h2>
            {isTrusted && (
              <span title="Successfully completed paid work and received a verified payout" className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-xs font-bold text-amber-950">
                ★
              </span>
            )}
          </div>

          {title && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mb-1">
              <Briefcase className="w-4 h-4" />
              {title}
            </div>
          )}

          <p className="text-emerald-600 dark:text-emerald-400 font-medium text-sm mb-4">{expertise}</p>

          {/* Stats Row */}
          <div className="flex items-center gap-6 mb-4 py-3 border-y border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-1.5">
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <span className="font-bold text-gray-900 dark:text-white">
                {rating > 0 ? Number(rating).toFixed(1) : 'New'}
              </span>
              {totalReviews > 0 && (
                <span className="text-sm text-gray-500">({totalReviews} reviews)</span>
              )}
            </div>
            {totalCalls > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <Phone className="w-4 h-4" />
                {totalCalls} calls
              </div>
            )}
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <Heart className="w-4 h-4" />
              {favoriteCount} favorites
            </div>
            <div className="ml-auto text-right">
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                ${formatRate(rate)}
              </span>
              <span className="text-sm text-gray-500">/min</span>
            </div>
          </div>

          {/* Industry */}
          {industry && industry !== 'Various Industries' && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mb-3">
              <MapPin className="w-4 h-4" />
              {industry}
            </div>
          )}

          {/* Sex — optional; only shown if provided */}
          {sex && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mb-3">
              <span className="text-base leading-none">👤</span>
              {sex}
            </div>
          )}

          {/* Bio */}
          {bio && (
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-5">
              {bio}
            </p>
          )}

          {/* Languages */}
          {expert.languages && expert.languages.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              {expert.languages.map((lang, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium"
                >
                  {lang}
                </span>
              ))}
            </div>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Reviews ({reviews.length})
              </p>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${i < Math.floor(review.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
                        />
                      ))}
                      <span className="text-xs text-gray-400 ml-1">
                        {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString() : ''}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{review.comment}</p>
                    )}
                    {review.callerName && (
                      <p className="text-xs text-gray-500 mt-1">— {review.callerName}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Social Links */}
          {(() => {
            const links = getSocialLinks(expert);
            if (links.length === 0) return null;
            return (
              <div className="flex flex-wrap gap-2 mb-4">
                {links.map((url, idx) => {
                  const platform = detectPlatform(url);
                  if (!platform) return null;
                  return (
                    <a
                      key={idx}
                      href={ensureProtocol(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${platform.colors.bg} ${platform.colors.text} hover:opacity-80 transition-opacity`}
                      title={platform.label}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill={platform.fill ? "currentColor" : "none"}
                        stroke={platform.fill ? "none" : "currentColor"}
                        strokeWidth={platform.fill ? undefined : "2"}
                        viewBox="0 0 24 24"
                        dangerouslySetInnerHTML={{ __html: platform.icon }}
                      />
                      {platform.label}
                    </a>
                  );
                })}
              </div>
            );
          })()}

          {/* Pricing info */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-5">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Pricing</p>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p>Rate: <span className="font-semibold">${formatRate(rate)}/minute</span></p>
              <p>Billing starts immediately when the Maven picks up</p>
              <p>Calls under 10 min: $0.30 minimum fee applies</p>
              <p>Plan access does not replace Maven per-minute billing</p>
            </div>
          </div>

          {/* Profile & Schedule actions */}
          {isApproved && expertId && (
            <div className="flex gap-3 mb-4">
              <Link
                href={`/expert-profile/${expertId}`}
                className="flex-1 inline-flex items-center justify-center py-3 rounded-2xl
                  border border-emerald-200 dark:border-emerald-800
                  text-emerald-700 dark:text-emerald-300 font-semibold
                  hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
              >
                View Full Profile
              </Link>
              {isSeekerRole && (
                <Link
                  href={`/expert-profile/${expertId}?openBooking=true`}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl
                    border border-purple-200 dark:border-purple-800
                    text-purple-700 dark:text-purple-300 font-semibold
                    hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                >
                  <CalendarClock className="w-4 h-4" />
                  Schedule
                </Link>
              )}
            </div>
          )}

          {canFavorite && (
            <button
              type="button"
              onClick={() => onToggleFavorite(expert)}
              disabled={favoriteLoading}
              className={`w-full mb-4 inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-semibold transition-colors ${
                isFavorited
                  ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800/60'
              } ${favoriteLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
              <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              {isFavorited ? 'Favorited' : 'Add to Favorites'} ({favoriteCount})
            </button>
          )}

          {/* Action Buttons — call is open to any signed-in user (maven→maven
              allowed); chat button inside stays seeker-only via canChat. */}
          {(canCall || canChat) && (
            <div className="flex gap-3">
              {canCall && (supportsAudio || (!supportsAudio && !supportsVideo)) && (
                <button
                  onClick={() => onCall(expert, 'audio')}
                  disabled={isOffline || isBusy}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl
                    bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base
                    transition-all active:scale-[0.98] shadow-lg shadow-emerald-500/25
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  <Phone className="w-5 h-5" />
                  Audio Call
                </button>
              )}
              {canCall && supportsVideo && (
                <button
                  onClick={() => onCall(expert, 'video')}
                  disabled={isOffline || isBusy || !canMakeVideoCalls}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl
                    bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base
                    transition-all active:scale-[0.98] shadow-lg shadow-blue-500/25
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  <Video className="w-5 h-5" />
                  Video Call
                </button>
              )}
              {canChat && (
                <button
                  onClick={handleStartChat}
                  disabled={chatLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl
                    bg-purple-600 hover:bg-purple-700 text-white font-semibold text-base
                    transition-all active:scale-[0.98] shadow-lg shadow-purple-500/25
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  <MessageCircle className="w-5 h-5" />
                  {chatLoading ? 'Opening...' : 'Chat'}
                </button>
              )}
            </div>
          )}

          {(!isApproved || isOffline) && (
            <p className="text-center text-sm text-gray-400 mt-3">
              {isApproved ? 'This person is currently offline. Try again later.' : 'New — not yet taking calls or bookings. You can send up to 5 shared free chat messages.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
